import { loginSchema } from '@nexo/types';
import { Button, Field, Input } from '@nexo/ui';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { errorMessage, errorsFromApi, useForm, validate } from '../../lib/forms';
import { useAuth } from './AuthProvider';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const location = useLocation();
  const form = useForm({ email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { data, errors } = validate(loginSchema, form.values);
    if (errors) return form.setErrors(errors);
    setLoading(true);
    setError(null);
    try {
      await login(data);
    } catch (err) {
      const fieldErrors = errorsFromApi(err);
      if (fieldErrors) form.setErrors(fieldErrors);
      else setError(errorMessage(err));
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={t('Inicia sesión')}
      subtitle={t('Bienvenido de nuevo a tu equipo.')}
      footer={
        <>
          {t('¿No tienes cuenta?')}{' '}
          <Link
            to={`/register${location.search}`}
            className="font-medium text-accent hover:underline"
          >
            {t('Regístrate')}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label={t('Email')} error={form.errors.email}>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              autoFocus
              value={form.values.email}
              onChange={(e) => form.set('email', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('Contraseña')} error={form.errors.password}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="current-password"
              value={form.values.password}
              onChange={(e) => form.set('password', e.target.value)}
            />
          )}
        </Field>
        <Link
          to="/forgot-password"
          className="-mt-2 self-end text-xs font-medium text-accent hover:underline"
        >
          {t('¿Olvidaste tu contraseña?')}
        </Link>
        {error && (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" loading={loading} className="mt-2 w-full">
          {t('Entrar')}
        </Button>
      </form>
    </AuthLayout>
  );
}
